/**
 * BudgetChip — surfaces today's per-workspace Gemini token spend
 * + remaining headroom against the daily cap (S2-7).
 *
 * Why it exists: without visibility users have no warning before a
 * heavy analyse / bid-matrix refresh trips the 429. The chip polls
 * /budget/today every 60s, stays subtle when usage is low, turns
 * amber at >= 70%, red at >= 90%, and shows an exhausted state
 * (with seconds-until-IST-midnight) when the cap is reached.
 *
 * Click → popover with token breakdown + request count. Admin-only
 * editing of the cap lands later (PATCH /budget exists; UI TBD).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Activity, AlertTriangle, Loader2 } from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";


const POLL_MS = 60_000;


function fmtTokens(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtRetryAfter(seconds) {
  if (seconds == null) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}


export default function BudgetChip() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const d = await v2Api.budget.today();
      setData(d);
      setError(false);
    } catch {
      // Endpoint can 401 on the public login route or 404 on older
      // backends pre-S2-7. Hide the chip silently — never break the
      // shell because of a status widget.
      setError(true);
    }
  }, []);

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [poll]);

  if (error || !data) return null;
  if (data.budget == null) return null;  // budget disabled via env

  const pct = data.budget > 0
    ? Math.min(100, Math.round((data.tokens_total / data.budget) * 100))
    : 0;

  let tone;
  if (data.exhausted) {
    tone = "bg-rose-100 text-rose-800 border-rose-300";
  } else if (pct >= 90) {
    tone = "bg-rose-50 text-rose-700 border-rose-200";
  } else if (pct >= 70) {
    tone = "bg-amber-50 text-amber-700 border-amber-200";
  } else {
    tone = "bg-white/10 text-white/80 border-white/20 hover:bg-white/15";
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-medium tabular-nums transition ${tone}`}
          title="Today's Gemini token usage"
          aria-label={`Gemini budget ${pct}% used`}
        >
          {data.exhausted
            ? <AlertTriangle className="w-3.5 h-3.5" />
            : <Activity className="w-3.5 h-3.5" />}
          <span>{pct}%</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="text-xs font-semibold text-slate-800 mb-1">
          Gemini budget — today ({data.date_ist})
        </div>
        <div className="text-[11px] text-slate-500 mb-2">
          Resets at midnight IST.
        </div>

        <div className="h-1.5 rounded bg-slate-100 overflow-hidden mb-2">
          <div
            className={`h-full ${
              data.exhausted ? "bg-rose-500"
                : pct >= 90 ? "bg-rose-400"
                : pct >= 70 ? "bg-amber-400"
                : "bg-emerald-400"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          <dt className="text-slate-500">Used</dt>
          <dd className="text-right text-slate-900 font-medium">
            {fmtTokens(data.tokens_total)} tok
          </dd>
          <dt className="text-slate-500">Budget</dt>
          <dd className="text-right text-slate-900 font-medium">
            {fmtTokens(data.budget)} tok
          </dd>
          <dt className="text-slate-500">Remaining</dt>
          <dd className="text-right text-slate-900 font-medium">
            {fmtTokens(data.remaining)} tok
          </dd>
          <dt className="text-slate-500">Requests</dt>
          <dd className="text-right text-slate-900 font-medium">
            {data.request_count}
          </dd>
          <dt className="text-slate-500">Prompt / output</dt>
          <dd className="text-right text-slate-900 font-medium">
            {fmtTokens(data.tokens_in)} / {fmtTokens(data.tokens_out)}
          </dd>
        </dl>

        {data.exhausted && (
          <div className="mt-3 text-[11px] rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-rose-800">
            Budget exhausted. New Gemini calls will return 429 until midnight IST.
          </div>
        )}
        {!data.exhausted && pct >= 70 && (
          <div className="mt-3 text-[11px] rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800">
            {pct}% used. Schedule heavy work (bid-matrix refresh, re-analyse)
            for after the IST midnight reset.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
